import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const MAX_POLLS = 8;
const POLL_MS = 2000;

export default function PaymentResult({ cancelled = false }) {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const home = user?.role === "clinician" ? "/clinician" : "/client";

  useEffect(() => {
    if (cancelled || !sessionId) return;
    let tries = 0;
    let stop = false;
    const poll = async () => {
      try {
        const r = await api.get(`/billing/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(r.data.payment_status)) {
          setState("failed");
          return;
        }
      } catch (e) {
        // transient — keep polling
      }
      if (!stop && ++tries < MAX_POLLS) setTimeout(poll, POLL_MS);
      else if (!stop) setState("timeout");
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, cancelled]);

  return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center relative z-10" data-testid="payment-result">
      <div className="warm-card p-10">
        {state === "checking" && (<>
          <Loader2 className="w-10 h-10 mx-auto text-amber-600 animate-spin" />
          <h1 className="font-display text-2xl font-bold text-stone-900 mt-4">Confirming your payment…</h1>
          <p className="text-sm text-stone-500 mt-2">This usually takes a couple of seconds.</p>
        </>)}
        {state === "paid" && (<>
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600" />
          <h1 className="font-display text-2xl font-bold text-stone-900 mt-4" data-testid="payment-success">You're all set.</h1>
          <p className="text-sm text-stone-600 mt-2">Thank you for supporting Root & Rose. Your access continues without interruption.</p>
        </>)}
        {state === "cancelled" && (<>
          <XCircle className="w-12 h-12 mx-auto text-stone-400" />
          <h1 className="font-display text-2xl font-bold text-stone-900 mt-4" data-testid="payment-cancelled">No charge was made.</h1>
          <p className="text-sm text-stone-600 mt-2">You can come back to this any time from the Billing page.</p>
        </>)}
        {(state === "failed" || state === "timeout") && (<>
          <XCircle className="w-12 h-12 mx-auto text-amber-600" />
          <h1 className="font-display text-2xl font-bold text-stone-900 mt-4" data-testid="payment-pending">We're still waiting on Stripe.</h1>
          <p className="text-sm text-stone-600 mt-2">If you completed payment, your plan will activate within a few minutes. Check the Billing page shortly.</p>
        </>)}
        <div className="flex justify-center gap-3 mt-6">
          <Link to="/billing" data-testid="payment-billing-link" className="text-sm font-semibold px-4 py-2 rounded-full bg-stone-100 hover:bg-stone-200">Billing</Link>
          <Link to={home} data-testid="payment-home-link" className="amber-btn text-sm">Back to dashboard</Link>
        </div>
      </div>
    </div>
  );
}
